import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import { call, restProps } from '../utils';
import Typography from '../Typography';
import styles from './Table.less';

const cx = classnames.bind(styles);
const EMPTY = () => null;

/**
 * **Access:**
 * ```js
 * import {Table} from '@iss/client-react-components'
 * ```
 * A Generic Table component that takes two props: `columns` & `items`.
 *
 * You may be tempted to extend this with sort handling.
 * Remember: components render their data and call actions to update that data.
 * Therefore the burden of "sort" falls on the store and the column components
 * to know how to display sort state.
 *
 * As a concession, it will pass all props not defined here to each
 * column/header/footer component.
 *
 *
 * To define a Column component, make its primary function render the cell in the
 * body of the table. Add static class properties called "HeaderComponent" and/or
 * "FooterComponent" if you need a header/footer. These static properties will be
 * used to flag whether or not to add the thead/tfoot.
 *
 * ```jsx
 * class ExampleColumn extends React.Component {
 * 	static propTypes = {
 *  		item: PropTypes.object.isRequired
 * 	}
 *
 * 	static HeaderComponent = () => <div/>	// These can be imported and assigned
 *  											// if you have a complex header/footer
 * 	static FooterComponent = () => <div/>
 *
 * 	render () {
 * 		return (
 * 			<div />
 * 		);
 * 	}
 * }
 * ```
 * @class Typography
 */
export default class Table extends React.PureComponent {
  static propTypes = {
    /** Use the "alternate" color scheme. */
    alternate: PropTypes.bool,

    /**
     * core prop, to allow customizing the table.
     * @ignore
     */
    className: PropTypes.string,

    /**
     * Collection of classNames to customize the table.
     *
     */
    classes: PropTypes.shape({
      /** className added to the container */
      container: PropTypes.string,
      /** className added to the table */
      table: PropTypes.string,
    }),

    /** The columns of the table. This array must be made of valid React Components (NOT JSX expressions) */
    columns: PropTypes.array.isRequired, //classes return 'function' as a typeof check

    /** Make the contents tighter (30px rows) vs the default 40px rows */
    condensed: PropTypes.bool,

    /** Display a message when there are no items */
    emptyMessage: PropTypes.string,

    /**
     * A callback to compute the category label for a row item. The function will recieve the row item as its only
     * argument and should return the renderable section label. If it does not return a primitive value (string,
     * number, etc) it should make sure to return the SAME instance of the category object/component for items that
     * belong to it. Otherwise, it will fail the `===` check and create a new section divider for every item.
     *
     * @type {function}
     * @param {*} item The value representing the row
     */
    getCategory: PropTypes.func,

    /** The list of items. All items in this iterable will be rendered. Filter and page outside of this component. */
    items: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.shape({
        map: PropTypes.func,
        reduce: PropTypes.func,
        length: PropTypes.number,
      }),
    ]).isRequired,

    /**
     * A callback to determine row if the row is selectable
     *
     * @type {function}
     * @param {*} item The value representing the row
     * @returns {boolean}
     */
    isSelectable: PropTypes.func,

    /**
     * A callback to determine row selection state.
     *
     * @type {function}
     * @param {*} item The value representing the row
     * @returns {boolean}
     */
    isSelected: PropTypes.func,

    /**
     * A callback for user selections
     *
     * @type {function}
     * @param {*} item The value representing the row
     * @param {Event} event The browser event that triggered the selection
     */
    onSelect: PropTypes.func,

    /**
     * Optional callback to compute a row class name.
     *
     * @type {function}
     * @param {*} item The value representing the row
     * @ignore
     */
    rowClassName: PropTypes.func,

    /** Enable even/odd row striping */
    striped: PropTypes.bool,

    /**
     * Core prop, to allow inline-styling the table container
     * @ignore
     */
    style: PropTypes.any,

    /**
     * Render a sticky header and expect the container's height to be restricted.
     * ***Note:*** Only in IE, the header will scroll with the table since it does not support `position: sticky`.
     */
    scrollable: PropTypes.bool,

    /** Keep the header visible on scroll */
    stickyHeader: PropTypes.bool,

    /** Render the first x columns fixed. Similar to sticky header, this feature isn't IE friendly. **/
    stickyColumns: PropTypes.number,
  };

  static defaultProps = {
    stickyHeader: true,
    stickyColumns: 0,
  };

  header = React.createRef();
  root = React.createRef();

  /**
   * @private
   * @type {boolean}
   */
  get hasFooter() {
    return (this.props.columns || []).some((x) => x.FooterComponent);
  }

  /**
   * @private
   * @type {boolean}
   */
  get hasHeader() {
    return (this.props.columns || []).some((x) => x.HeaderComponent);
  }

  get domNode() {
    return this.root.current;
  }

  componentDidMount() {
    global.addEventListener('scroll', this.handleScroll);
    const { domNode } = this;
    const { stickyColumns } = this.props;

    if (stickyColumns) {
      const { left: tableOffset } = domNode.getBoundingClientRect();
      const headers = [...domNode.querySelectorAll('th')];
      const columnOffsets = headers.map((header) => header.getBoundingClientRect().left - tableOffset - 1);
      const scroller = domNode.querySelector(`.${cx({ scroller: true })}`);
      const shadowOffset = columnOffsets[stickyColumns];
      // Set the position of "drop shadow" that is created by background gradients.
      scroller.style.backgroundPosition = `${shadowOffset}px 0, ${shadowOffset}px 0`;

      if (stickyColumns > 1) {
        // For each sticky column after the first, left has to be set otherwise they just pile up.
        [...this.domNode.querySelectorAll('tr')].forEach((row) =>
          [...row.querySelectorAll('th, td')].slice(0, stickyColumns).forEach((cell, index) => {
            cell.style.left = `${columnOffsets[index]}px`;
          })
        );
      }
    }
  }

  componentWillUnmount() {
    global.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Handles Calling the onSelect callback prop.
   *
   * @private
   * @method onSelect
   * @param  {*} item The row value
   * @param  {Event} e    The Browser Event
   * @return {void}
   */
  onSelect = (item, e) => {
    e.stopPropagation();
    e.preventDefault();
    call.debounced(this.props.onSelect, item, e);
  };

  handleScroll = () => {
    const {
      props: { stickyHeader },
      root: { current: root },
      header: { current: head },
    } = this;

    try {
      /* istanbul ignore else */
      if (stickyHeader) {
        const top = Math.max(0, 0 - root.getBoundingClientRect().top);
        head.style.transform = `translate3d(0,${top}px,0)`;
      }
    } catch {
      //meh
    }
  };

  render() {
    const {
      items: { length } = {},
      emptyMessage,
      alternate,
      className,
      condensed,
      scrollable,
      stickyColumns,
      striped,
      style,
    } = this.props;

    const empty = !length;

    const classes = cx('root', {
      empty,
      alternate,
      condensed,
      scrollable,
      striped,
    });

    const props = { ...restProps(Table, this.props), alternate, condensed };

    return (
      <div className={cx(styles.container, className)} style={style} ref={this.root}>
        {scrollable ? (
          <>
            <div className={cx(styles.scroller, { 'sticky-columns': stickyColumns })}>
              {this.renderTable(classes, props)}
            </div>
          </>
        ) : (
          <>{this.renderTable(classes, props)}</>
        )}

        {empty && emptyMessage && (
          <Typography as="div" face="heading2" className={cx('empty-state')}>
            {emptyMessage}
          </Typography>
        )}
      </div>
    );
  }

  renderTable(classes, props) {
    return (
      <table className={classes}>
        {this.renderData(props)}
        {this.renderHeader(props)}
        {this.renderFooter(props)}
      </table>
    );
  }

  renderData(props) {
    const { columns = [], getCategory = () => null, items = [] } = this.props;

    const peek = (a) => a[a.length - 1];
    const bins = (items.reduce ? items : Array.from(items)).reduce((a, item) => {
      const category = getCategory(item);
      if (!peek(a) || category !== peek(a).category) {
        a.push({ category, items: [] });
      }
      peek(a).items.push(item);
      return a;
    }, []);

    return bins.map(({ category, items: rows }, cid) => (
      <React.Fragment key={cid}>
        <Category className={styles.category} label={category} columnSpan={columns.length} />

        <tbody>{rows.map((item, row) => this.renderRow(item, row, props))}</tbody>
      </React.Fragment>
    ));
  }

  renderRow(item, row, props) {
    const {
      columns,
      isSelectable = () => !!this.props.onSelect,
      isSelected,
      items,
      rowClassName = () => '',
    } = this.props;
    const selectable = call(isSelectable, item);
    const selected = call(isSelected, item);

    const cellProps = {
      ...props,
      item,
      selected,
      onSelect: this.onSelect,
    };

    return (
      <tr
        key={row}
        onClick={(e) => this.onSelect(item, e)}
        className={cx(call(rowClassName, item, row, items), {
          row: true,
          selectable,
        })}
      >
        {columns.map((Cell, col) => this.renderCell(Cell, col, cellProps))}
      </tr>
    );
  }

  renderCell(Cell, col, props) {
    const { selected } = props;
    const { stickyColumns } = this.props;
    const sticky = stickyColumns > col;

    const wrapper = {
      className: cx(Cell.cssClassName, {
        cell: true,
        selected,
        sticky,
      }),
      key: col,
    };

    return Cell.rendersContainer ? (
      <Cell {...wrapper} {...props} />
    ) : (
      <td {...wrapper}>
        <Cell {...props} />
      </td>
    );
  }

  renderHeader(props) {
    return !this.hasHeader ? null : (
      <thead ref={this.header}>{this.renderHeadingRow('HeaderComponent', styles.header, props)}</thead>
    );
  }

  renderFooter(props) {
    return !this.hasFooter ? null : <tfoot>{this.renderHeadingRow('FooterComponent', styles.footer, props)}</tfoot>;
  }

  renderHeadingRow(fieldName, className, props) {
    const { columns, stickyColumns } = this.props;

    return (
      <tr>
        {columns.map(({ [fieldName]: Component = EMPTY, cssClassName }, i) => {
          const sticky = i < stickyColumns;
          if (Component.rendersContainer) {
            return <Component key={i} thClassName={cx({ sticky })} className={cx(className)} {...props} />;
          }

          return (
            <th key={i} className={cx(cssClassName, { sticky })}>
              <Component className={className} {...props} />
            </th>
          );
        })}
      </tr>
    );
  }
}

Category.propTypes = {
  /** @ignore */
  className: PropTypes.string,

  /**
   * Number of columns to span
   * @ignore
   */
  columnSpan: PropTypes.number,

  /** Text to show in the divider, can be any renderable thing. */
  label: PropTypes.node,
};

/**
 * Category row component.
 *
 * This is internal to the `Table` component. It is not exposed.
 * @private
 * @returns {Component} rendered content
 */
function Category({ className, columnSpan, label }) {
  return !label ? null : (
    <tbody className={cx(styles.categoryGroup, className)}>
      <tr>
        <td colSpan={columnSpan}>
          <Typography.SubText2 className={styles.text}>{label}</Typography.SubText2>
        </td>
      </tr>
    </tbody>
  );
}
